<template>
  <NMap :boundsModel="windowBoundsModel" :focusModel="defaultFocusModel" class="h-100">
    <NTilesLayer/>
    <NVectorTilesLayer @hoverFeature="hoverFeatureHandler" :layer="hs6BasinsLayer" :style="{ opacity: sliderValue / 100 }">
      <NCanvasSymbolizer style="stroke: #fff" :style="{ 'stroke-width': strokeWidth }" :cssFunction="fillCssFunction"/>
    </NVectorTilesLayer>
    <NTilesLayer :layer="hs4BasinsLayer" style="pointer-events: none"/>
    <NPathLayer v-if="feature" :x="tileX" :y="tileY" :scaling="tileScaling" :path="featureGeometry" style="pointer-events: none; fill: none; stroke-width: 5px; stroke-linejoin: round; filter: blur(3px);" :style="{ stroke: (feature.properties[crop.code + '_' + substance.code + '_kg_ha']) ? 'yellow' : 'silver' }"/>
    <NPathLayer v-if="feature" :x="tileX" :y="tileY" :scaling="tileScaling" :path="featureGeometry" style="pointer-events: none; fill: none; stroke-width: 2px; stroke-linejoin: round" :style="{ stroke: (feature.properties[crop.code + '_' + substance.code + '_kg_ha']) ? 'var(--bs-body-color)' : 'grey' }"/>
  </NMap>

  <RouterLink to="/" class="btn btn-outline-secondary"><i class="fa-solid fa-arrow-left"></i> <i class="fa-solid fa-house"></i></RouterLink>

  <div class="left-side">
    <div class="legend-box">
      <h5>
        <span style="font-size: 1.5rem; font-variant: small-caps;">{{ substance.name }} load</span><br/>
        <span style="font-variant: small-caps;">to freshwater</span><br/>
        <small>growing {{ crop.name }}</small>
      </h5>
      <div class="legend-content">
        <div class="legend-class">
          <span class="legend-color" style="background-color: #7a0403" :style="{ opacity: sliderValue / 100 }"></span>
          <span class="grow"> > 100</span>
          <span>kg/ha</span>
        </div>
        <div class="legend-class">
          <span class="legend-color" style="background-color: #c22a05" :style="{ opacity: sliderValue / 100 }"></span>
          <span> > 50</span>
        </div>
        <div class="legend-class">
          <span class="legend-color" style="background-color: #e4521e" :style="{ opacity: sliderValue / 100 }"></span>
          <span> > 20</span>
        </div>
        <div class="legend-class">
          <span class="legend-color" style="background-color: #465bd3" :style="{ opacity: sliderValue / 100 }"></span>
          <span> > 7.0</span>
        </div>
        <div class="legend-class">
          <span class="legend-color" style="background-color: #289ceb" :style="{ opacity: sliderValue / 100 }"></span>
          <span> > 2.0</span>
        </div>
        <div class="legend-class">
          <span class="legend-color" style="background-color: #57d8dc" :style="{ opacity: sliderValue / 100 }"></span>
          <span> > 0.4</span>
        </div>
        <div class="legend-class">
          <span class="legend-color" style="background-color: #94ebd5" :style="{ opacity: sliderValue / 100 }"></span>
          <span> &le; 0.4</span>
        </div>
      </div>
      <div class="separator"></div>
      <div class="transparency">
        <label for="range" class="form-label"><small>Transparency</small></label><br/>
        <input type="range" id="range" v-model="sliderValue" title="Set the opacity of these colours" class="form-range"/>
      </div>
    </div>
    <div class="map-actions">
      <div class="zoom-action" @click="zoomIn" title="Zoom in">
        <img src="../assets/img/plus.svg" alt />
      </div>
      <div class="zoom-action" @click="zoomOut" title="Zoom out">
        <img src="../assets/img/minus.svg" alt />
      </div>
    </div>
  </div>

  <div class="right-side">
    <div class="list-group">
      <li class="list-group-item list-group-item-header">
        <b>Nutrient</b>
      </li>
      <label v-for="substance1 of substances" role="button" title="Select this nutrient" class="list-group-item">
        <input type="radio" v-model="substance" :value="substance1" class="form-check-input me-1">
        {{ substance1.name }}
      </label>
    </div>    
    <div class="list-group shrink">
      <li class="list-group-item list-group-item-header">
        <b>Crop Group</b>
      </li>
      <div class="shrink">
        <button v-for="crop1 of crops" @click="crop = crop1" title="Select this crop" class="list-group-item list-group-item-action" :class="{'active': crop == crop1}">
          {{ crop1.name }}
        </button>
      </div>
    </div>
  </div>

  <div v-if="feature" class="val-box">
    <b><small>River Basin {{ feature.properties.id }}</small></b>
    <div v-if="feature.properties[crop.code + '_' + substance.code + '_kg_ha']">{{ getSignificantNumberString(feature.properties[crop.code + "_" + substance.code + "_kg_ha"]) }} kg of {{ substance.code.toUpperCase() }} per ha</div>
    <div v-else class="text-muted">(no data)</div>
  </div>
</template>

<script>
import { jsPDF } from "jspdf";
import { CenterScale, FocusModel, Layer, defaultFocusModel, windowBoundsModel } from "vue-niney";
import NCanvasSymbolizer from "vue-niney/NCanvasSymbolizer.vue";
import NMap from "vue-niney/NMap.vue";
import NPathLayer from "./NPathLayer.vue";
import NTilesLayer from "vue-niney/NTilesLayer.vue";
import NVectorTilesLayer from "@/components/NVectorTilesLayer.vue";
import { getSignificantNumberString } from "../services/data-tools";

export default {
  name: "Map",
  components: {
    NCanvasSymbolizer,
    NMap,
    NPathLayer,
    NTilesLayer,
    NVectorTilesLayer
  },
  data() {
    return {
      windowBoundsModel,
      defaultFocusModel,
      hs4BasinsLayer: new Layer("BasinsTiles"),
      hs6BasinsLayer: new Layer("Hs6Basins"),
      substances: [
        { code: "n", name: "nitrogen", capitalized: "Nitrogen", width: "132px" },
        { code: "p", name: "phosphorus", capitalized: "Phosphorus", width: "158px" }
      ],
      substance: null,
      crops: [
        { code: "cer", name: "cereal" },
        { code: "rtu", name: "roots & tubers" },
        { code: "suc", name: "sugar crops" },
        { code: "pul", name: "pulses" },
        { code: "nut", name: "nuts" },
        { code: "olc", name: "oil crops" },
        { code: "vgt", name: "vegetables" },
        { code: "frt", name: "fruit" },
        { code: "stm", name: "stimulants" },
        { code: "spc", name: "spices" },
        { code: "fib", name: "fibre crops" },
        { code: "oth", name: "other crops" }
      ],
      crop: null,
      tileX: null,
      tileY: null,
      tileZ: null,
      featureGeometry: null,
      feature: null,
      sliderValue: 100
    }
  },
  props: {
    msg: String
  },
  computed: {
    strokeWidth() {
      const scale = this.defaultFocusModel.animationCenterScale.scale;
      const minScale = this.defaultFocusModel.minScale;
      if (scale < minScale * 1.5) {
        return 1.5;
      }
      if (scale < minScale * 3) {
        return 1;
      }
      return 0.5;
    },
    fillCssFunction() {
      const propertyName = this.crop.code + "_" + this.substance.code + "_kg_ha";
      return (css, feature) => {
        css.visibility = "visible";
        const val = feature.feature.properties[propertyName];
        if (val > 100) {
          css.fill = "#7a0403";
        } else if (val > 50) {
          css.fill = "#c22a05";
        } else if (val > 20) {
          css.fill = "#e4521e";
        } else if (val > 7) {
          css.fill = "#465bd3";
        } else if (val > 2) {
          css.fill = "#289ceb";
        } else if (val > 0.4) {
          css.fill = "#57d8dc";
        } else if (val > 0) {
          css.fill = "#94ebd5";
        } else {
          css.visibility = "hidden";
        }
      }
    }
  },
  created() {
    this.substance = this.substances[0];
    this.crop = this.crops[0];

    this.hs4BasinsLayer.baseURL = "https://tools.waterfootprint.org/tiles/hbasins/";

    //this.hs6BasinsLayer.baseURL = "http://172.19.0.5:8080/vectortiles/rest/tiles/";
    this.hs6BasinsLayer.baseURL = "https://tools.waterfootprint.org/vectortiles/rest/tiles/";
    this.hs6BasinsLayer.urlExtension = "$Z/$X/$Y.vector.pbf";
    this.hs6BasinsLayer.visible = true;
  },
  methods: {
    hoverFeatureHandler(tile, feature) {
      this.tileX = tile?.x;
      this.tileY = tile?.y;
      this.tileScaling = tile?.scaling * 256 / 4096;
      this.featureGeometry = feature?.geometry.replace(/K/g, "L");
      this.feature = feature;
    },
    zoomIn: function() {
      var cs = defaultFocusModel.animationCenterScale;
      defaultFocusModel.setCenterScale(
        new CenterScale(cs.centerX, cs.centerY, cs.scale / 2),
        FocusModel.ALWAYS_NEAREST
      );
    },
    zoomOut: function() {
      var cs = defaultFocusModel.animationCenterScale;
      defaultFocusModel.setCenterScale(
        new CenterScale(cs.centerX, cs.centerY, cs.scale * 2),
        FocusModel.ALWAYS_NEAREST
      );
    },

    getSignificantNumberString(n) {
      return getSignificantNumberString(n, 2);
    },



    draw() {
var c = document.getElementById("c");
var ctx = c.getContext("2d");
ctx.beginPath();
ctx.rect(20, 20, 150, 100);
ctx.stroke();    },
    doThat() {
// Default export is a4 paper, portrait, using millimeters for units
const doc = new jsPDF();

doc.text("Hello world!", 10, 10);

const canvas = document.getElementById("c");
const ctx = canvas.getContext("2d");
const imgData = canvas.toDataURL('image/png');
doc.addImage(imgData, 'PNG', 10, 10);

doc.save("a4.pdf");
    }
  }
}
</script>

<style scoped lang="scss">
.btn-outline-secondary {
  position: absolute;
  left: 20px;
  top: 20px;
}
.left-side {
  display: flex;
  position: absolute;
  left: 20px;
  bottom: 20px;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
  .legend-box {
    display: flex;
    border: 2px solid var(--bs-primary);
    border-radius: var(--bs-border-radius);
    padding: 10px 15px 3px;
    background-color: #fff;
    pointer-events: all;
    flex-direction: column;
    align-items: center;
    gap: 9px;
    h5 {
      text-align: center;
      line-height: 1.35;
      margin-bottom: 6px;
    }
    .legend-content {
      width: 150px;
      .legend-class {
        display: flex;
        margin-bottom: 6px;
        align-items: center;
        gap: 8px;
        .legend-color {
          display: inline-block;
          width: 32px;
          height: 16px;
        }
        .grow {
          flex-grow: 1;
        }
      }
    }
    .separator {
      width: calc(100% + 30px);
      height: 1px;
      border-top: 1px solid var(--bs-primary);
    }
    .transparency {
      width: 150px;
      .form-label {
        margin-bottom: 1px;
      }
      .form-range {
        cursor: pointer;
      }
    }
  }
  .map-actions {
    .zoom-action {
      margin-top: 10px;
      pointer-events: all;
      cursor: pointer;
      img {
        width: 30px;
      }
    }
  }
}
.right-side {
  display: flex;
  position: absolute;
  right: 20px;
  top: 20px;
  max-height: calc(100% - 40px);
  flex-direction: column;
  gap: 18px;
  pointer-events: none;
  .list-group {
    border: 2px solid var(--bs-primary);
    padding: 1px;
    background-color: #fff;
    pointer-events: all;
    .list-group-item-header {
      color: #fff;
      background-color: #7a0403;
    }
    .list-group-item.active {
      border-color: var(--bs-list-group-border-color);
    }
  }
  .shrink {
    flex-shrink: 1;
    overflow: auto;
  }
}
.val-box {
  position: absolute;
  left: calc(50% - 75px);
  bottom: 20px;
  border: solid 1px var(--bs-body-color);
  border-radius: 3px;
  padding: 12px 15px;
  background-color: rgba(255, 255, 255, 0.85);
  text-wrap: nowrap;
  pointer-events: none;
}
</style>
